import ContentWrapper from '../views/components/ContentWrapper';
import DataSubMenu from '../views/components/DataSubMenu';
import { useEffect, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    PointElement,
    LineElement,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    BarElement,
    ArcElement
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useUserState } from '../views/components/UserStateProvider';
import UpArrow from '../images/up-arrow-green-md.svg';
import DownArrow from '../images/down-arrow-red-md.svg';
import { getStatistics } from '../data/dataApi';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

const DashboardHome = () => {
    const initialStatictic = {
        value: null,
        trend: null
    };
    const [sessions, setSessions] = useState(initialStatictic);
    const [questionAnswered, setQuestionsAnswered] = useState(initialStatictic);
    const [satisfactoryAnswers, setSatisfactoryAnswers] = useState(initialStatictic);
    const [satisfactionRate, setSatisfactionRate] = useState(initialStatictic);
    const [chartLabels, setChartLabels] = useState([]);
    const [sessionChartData, setSessionChartData] = useState([]);
    const [productSearchSessionChartData, setProductSearchSessionChartData] = useState([]);
    const [navigatorSessionChartData, setNavigatorSessionChartData] = useState([]);
    const [chatbotSessionChartData, setChatbotSessionChartData] = useState([]);
    const [upFeedbackChartData, setUpFeedbackChartData] = useState([]);
    const [downFeedbackChartData, setDownFeedbackChartData] = useState([]);

    const subNavOptions = ['Past Week', 'Past Month', 'Past Year', 'All Time'];
    const { theme } = useUserState();

    const [activeTab, setActiveTab] = useState(subNavOptions[0]);

    const defaultChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: true,
                color: theme === 'light' ? '#24439c' : '#fff',
                font: {
                    size: 20,
                    weight: 400,
                },
                align: 'start',
                padding: 35,
            },
            legend: {
                labels: {
                    color: theme === 'light' ? '#24439c' : '#fff',
                    font: {
                        size: 14,
                    },
                    usePointStyle: true,
                    pointStyle: 'circle',
                    boxWidth: 8,
                    boxHeight: 8,
                }
            }
        },
        scales: {
            x: {
                grid: {
                    color: function (context) {
                        if (context.tick) {
                            if (context.tick.value === 0) {
                                return theme === 'light' ? "#d8dfe3" : "#404040";
                            } else {
                                return theme === 'light' ? "#d8dfe3" : "#282828";
                            }
                        }

                    },
                },
                ticks: {
                    color: "#B3B3B3",
                    font: {
                        size: 14,
                    }                }
            },
            y: {
                grid: {
                    color: function (context) {
                        if (context.tick) {
                            if (context.tick.value === 0) {
                                return theme === 'light' ? "#d8dfe3" : "#404040";
                            } else {
                                return theme === 'light' ? "#d8dfe3" : "#282828";
                            }
                        }
                    },
                },
                ticks: {
                    color: "#B3B3B3",
                    font: {
                        size: 14
                    },
                    stepSize: 1
                }
            },
        }
    };

    const productSearchSessionsChartData = {
        labels: chartLabels,
        datasets: [
            {
                label: 'Product Search Sessions',
                data: productSearchSessionChartData,
                borderColor: '#3B61CE',
                backgroundColor: '#3B61CE',
                borderWidth: 1,
                radius: 5,
            }
        ],
    };

    const chatbotSessionsChartData = {
        labels: chartLabels,
        datasets: [
            {
                label: 'Chatbot Sessions',
                data: chatbotSessionChartData,
                borderColor: '#98aff2',
                backgroundColor: '#98aff2',
                borderWidth: 1,
                radius: 5,
            }
        ],
    };

    const navigatorSessionsChartData = {
        labels: chartLabels,
        datasets: [
            {
                label: 'Navigator Sessions',
                data: navigatorSessionChartData,
                borderColor: '#9118be',
                backgroundColor: '#9118be',
                borderWidth: 1,
                radius: 5,
            },
        ],
    };

    const feedbackChartData = {
        labels: chartLabels,
        datasets: [
            {
                label: 'Up Rating',
                data: upFeedbackChartData,
                borderColor: '#00AB79',
                backgroundColor: '#00AB79',
                barThickness: 12,
            },
            {
                label: 'Down Rating',
                data: downFeedbackChartData,
                borderColor: '#3B61CE',
                backgroundColor: '#3B61CE',
                barThickness: 12,
            },
        ],
    };

    useEffect(() => {
        (async () => {
            const response = await getStatistics(activeTab);

            const getTrend = (val, compareVal) => {
                if(val > compareVal) {
                    return 'up';
                } else if(val < compareVal) {
                    return 'down';
                }

                return;
            };

            if(response.success) {
                const data = response.data.rows[0];

                setSessions({
                    value: data.current_sessions,
                    trend: getTrend(data.current_sessions, data.previous_sessions)
                });

                setQuestionsAnswered({
                    value: data.questions_answered_current,
                    trend: getTrend(Number(data.questions_answered_current), Number(data.questions_answered_previous))
                });

                const satisfactoryAnswersCurrent = Number(data.questions_answered_current) - Number(data.unsatisfactory_answers_current);
                const unsatisfactoryAnswersCurrent = Number(data.unsatisfactory_answers_current);
                const totalAnswersCurrent = satisfactoryAnswersCurrent + unsatisfactoryAnswersCurrent;
                
                setSatisfactoryAnswers({
                    value: satisfactoryAnswersCurrent,
                    trend: getTrend(Number(data.satisfactory_answers_previous), Number(data.satisfactory_answers_previous))
                });

                const satisfactoryPercentageCurrent = totalAnswersCurrent > 0 ? (satisfactoryAnswersCurrent / totalAnswersCurrent * 100).toFixed(1) : null;
                
                const satisfactoryAnswersPrevious = Number(data.satisfactory_answers_previous);
                const unsatisfactoryAnswersPrevious = Number(data.unsatisfactory_answers_previous);
                const totalAnswersPrevious = satisfactoryAnswersPrevious + unsatisfactoryAnswersPrevious;
                const satisfactoryPercentagePrevious = totalAnswersPrevious > 0 ? (satisfactoryAnswersPrevious / totalAnswersPrevious * 100).toFixed(1) : null;

                setSatisfactionRate({
                    value: satisfactoryPercentageCurrent ? `${satisfactoryPercentageCurrent}%` : "N/A",
                    trend: satisfactoryPercentageCurrent && satisfactoryPercentagePrevious ? getTrend(Number(satisfactoryPercentageCurrent), Number(satisfactoryPercentagePrevious)) : null
                });

                const getCount = (sessions, type) => sessions.slice(0, sessions.length).map(session => session[type]);

                if(activeTab === "Past Week") {
                    const labels = ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];
                    let today = new Date();
                    let lastSevenDays = [];
        
                    for (let i = 6; i >= 0; i--) {
                        let day = new Date();
                        day.setDate(today.getDate() - i);
                        lastSevenDays.push(labels[day.getDay()]);
                    }

                    setSessionChartData(getCount(data.sessions_by_day, "session_count"));
                    setProductSearchSessionChartData(getCount(data.product_search_sessions_by_day, "session_count"));
                    setNavigatorSessionChartData(getCount(data.navigator_sessions_by_day, "session_count"));
                    setChatbotSessionChartData(getCount(data.chatbot_sessions_by_day, "session_count"));

                    setUpFeedbackChartData(getCount(data.feedback_by_day, "up_count"));
                    setDownFeedbackChartData(getCount(data.feedback_by_day, "down_count"));
        
                    setChartLabels(lastSevenDays);
                } else if (activeTab === "Past Month") {
                    let today = new Date();
                    let lastFourWeeks = [];
                    let oneMonthAgo = new Date(today);
                    oneMonthAgo.setMonth(today.getMonth() - 1);
        
                    let weekStart = new Date(today);
                    while (weekStart >= oneMonthAgo) {
                        lastFourWeeks.unshift(weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
                        weekStart = new Date(weekStart);
                        weekStart.setDate(weekStart.getDate() - 7);
                    }
        
                    if (lastFourWeeks[0] !== oneMonthAgo.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })) {
                        lastFourWeeks.unshift(oneMonthAgo.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
                    }

                    setSessionChartData(getCount(data.sessions_by_week, "session_count"));
                    setProductSearchSessionChartData(getCount(data.product_search_sessions_by_week, "session_count"));
                    setNavigatorSessionChartData(getCount(data.navigator_sessions_by_week, "session_count"));
                    setChatbotSessionChartData(getCount(data.chatbot_sessions_by_week, "session_count"));

                    setUpFeedbackChartData(getCount(data.feedback_by_week, "up_count"));
                    setDownFeedbackChartData(getCount(data.feedback_by_week, "down_count"));
        
                    setChartLabels(lastFourWeeks);
                } else if (activeTab === "Past Year") {
                    let today = new Date();
                    let lastTwelveMonths = [];

                    for (let i = 0; i < 13; i++) {
                        let month = new Date(today);
                        month.setMonth(today.getMonth() - i);
                        lastTwelveMonths.unshift(month.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
                    }

                    setSessionChartData(getCount(data.sessions_by_month, "session_count"));
                    setProductSearchSessionChartData(getCount(data.product_search_sessions_by_month, "session_count"));
                    setNavigatorSessionChartData(getCount(data.navigator_sessions_by_month, "session_count"));
                    setChatbotSessionChartData(getCount(data.chatbot_sessions_by_month, "session_count"));

                    setUpFeedbackChartData(getCount(data.feedback_by_month, "up_count"));
                    setDownFeedbackChartData(getCount(data.feedback_by_month, "down_count"));
        
                    setChartLabels(lastTwelveMonths);
                } else if (activeTab === "All Time") {
                    let yearLabels = data.sessions_by_year.map(session => {
                        let date = new Date(session.date);
                        return date.getUTCFullYear().toString();
                    });
                
                    setSessionChartData(getCount(data.sessions_by_year, "session_count"));
                    setProductSearchSessionChartData(getCount(data.product_search_sessions_by_year, "session_count"));
                    setNavigatorSessionChartData(getCount(data.navigator_sessions_by_year, "session_count"));
                    setChatbotSessionChartData(getCount(data.chatbot_sessions_by_year, "session_count"));

                    setUpFeedbackChartData(getCount(data.feedback_by_year, "up_count"));
                    setDownFeedbackChartData(getCount(data.feedback_by_year, "down_count"));

                    setChartLabels(yearLabels);
                }
            }
        })();
    }, [activeTab]);

    return (
        <ContentWrapper className="scrollable">
            <DataSubMenu subNavOptions={subNavOptions} activeTab={activeTab} setActiveTab={setActiveTab} />

            <div className="wrapper">
                <div id="statistics-wrapper" className="d-flex flex-wrap tif-mb-md-30 tif-mb-20">
                    <div className="statistic">
                        <p className="label tif-color-white tif-light-color-oscar tif-mb-0">Sessions</p>
                        {
                            sessions.value ?
                                <p className="result tif-color-white tif-light-color-november tif-font-xl tif-mb-0 text-nowrap">
                                    <span className="trend-wrapper position-relative tif-pr-md-25 tif-pr-20">
                                        { sessions.value }
                                        {
                                            activeTab !== 'All Time' && sessions.trend &&
                                                <>
                                                    <img src={ UpArrow } className={`up-trend position-absolute end-0 ${ sessions.trend !== "up" && 'opacity-25' }`} />
                                                    <img src={ DownArrow } className={`down-trend position-absolute end-0 ${ sessions.trend !== "down" && 'opacity-25' }`} />
                                                </>
                                        }
                                    </span>
                                </p>
                            :
                                <></>
                        }
                    </div>
                    <div className="statistic">
                        <p className="label tif-color-white tif-light-color-oscar tif-mb-0">Questions Answered</p>
                        {
                            questionAnswered.value ?
                                <p className="result tif-color-white tif-light-color-november tif-font-xl tif-mb-0 text-nowrap">
                                    <span className="trend-wrapper position-relative tif-pr-md-25 tif-pr-20">
                                        { questionAnswered.value }
                                        {
                                            activeTab !== 'All Time' && questionAnswered.trend &&
                                                <>
                                                    <img src={ UpArrow } className={`up-trend position-absolute end-0 ${ questionAnswered.trend !== "up" && 'opacity-25' }`} />
                                                    <img src={ DownArrow } className={`down-trend position-absolute end-0 ${ questionAnswered.trend !== "down" && 'opacity-25' }`} />
                                                </>
                                        }
                                    </span>
                                </p>
                            :
                                <></>
                        }
                    </div>
                    <div className="statistic">
                        <p className="label tif-color-white tif-light-color-oscar tif-mb-0">Satisfactory Answers</p>
                        {
                            satisfactoryAnswers.value !== undefined ?
                                <p className="result tif-color-white tif-light-color-november tif-font-xl tif-mb-0 text-nowrap">
                                    <span className="trend-wrapper position-relative tif-pr-md-25 tif-pr-20">
                                        { satisfactoryAnswers.value }
                                        {
                                            activeTab !== 'All Time' && satisfactoryAnswers.trend &&
                                                <>
                                                    <img src={ UpArrow } className={`up-trend position-absolute end-0 ${ satisfactoryAnswers.trend !== "up" && 'opacity-25' }`} />
                                                    <img src={ DownArrow } className={`down-trend position-absolute end-0 ${ satisfactoryAnswers.trend !== "down" && 'opacity-25' }`} />
                                                </>
                                        }
                                    </span>
                                </p>
                            :
                                <></>
                        }
                    </div>
                    <div className="statistic">
                        <p className="label tif-color-white tif-light-color-oscar tif-mb-0">Satisfaction Rate</p>
                        {
                            satisfactionRate.value ?
                                <p className="result tif-color-white tif-light-color-november tif-font-xl tif-mb-0 text-nowrap">
                                    <span className="trend-wrapper position-relative tif-pr-md-25 tif-pr-20">
                                        { satisfactionRate.value }
                                        {
                                            activeTab !== 'All Time' && satisfactionRate.trend &&
                                                <>
                                                    <img src={ UpArrow } className={`up-trend position-absolute end-0 ${ satisfactionRate.trend !== "up" && 'opacity-25' }`} />
                                                    <img src={ DownArrow } className={`down-trend position-absolute end-0 ${ satisfactionRate.trend !== "down" && 'opacity-25' }`} />
                                                </>
                                        }
                                    </span>
                                </p>
                            :
                                <></>
                        }
                    </div>
                </div>

                <div className="chart-wrapper position-relative tif-mb-md-30 tif-mb-20">
                    {
                        sessionChartData.length && chartLabels.length ?
                            <Line options={{ ...defaultChartOptions, plugins: {
                                ...defaultChartOptions.plugins,
                                title: {
                                    ...defaultChartOptions.plugins.title,
                                    text: "Product Search Sessions",
                                },
                                legend: {
                                    display: false
                                }
                            }}} data={productSearchSessionsChartData} />
                        :
                        <></>
                    }
                </div>
                <div className="chart-wrapper position-relative tif-mb-md-30 tif-mb-20">
                    {
                        sessionChartData.length && chartLabels.length ?
                            <Line options={{ ...defaultChartOptions, plugins: {
                                ...defaultChartOptions.plugins,
                                title: {
                                    ...defaultChartOptions.plugins.title,
                                    text: "Chatbot Sessions",
                                },
                                legend: {
                                    display: false
                                }
                            }}} data={chatbotSessionsChartData} />
                        :
                        <></>
                    }
                </div>
                <div className="chart-wrapper position-relative tif-mb-md-30 tif-mb-20">
                    {
                        sessionChartData.length && chartLabels.length ?
                            <Line options={{ ...defaultChartOptions, plugins: {
                                ...defaultChartOptions.plugins,
                                title: {
                                    ...defaultChartOptions.plugins.title,
                                    text: "Navigator Sessions",
                                },
                                legend: {
                                    display: false
                                }
                            }}} data={navigatorSessionsChartData} />
                        :
                        <></>
                    }
                </div>
                <div className="chart-wrapper position-relative tif-mb-md-30 tif-mb-20">
                    {
                        upFeedbackChartData.length && downFeedbackChartData.length && chartLabels.length ?
                            <Line options={{ ...defaultChartOptions, plugins: {
                                ...defaultChartOptions.plugins,
                                title: {
                                    ...defaultChartOptions.plugins.title,
                                    text: "Feedback",
                                },
                            }}} data={feedbackChartData} />
                        :
                            <></>
                    }
                </div>
            </div>
        </ContentWrapper>
    );
};

export default DashboardHome;